import { computed, ref, ComputedRef, Ref } from 'vue';
import { CombinationsConfiguratorFormData } from '@/modules/ConfiguratorCombinations/interfaces/CombinationsInterfaces';
import {
    isAssemblyStep1Completed,
    isAssemblyStep2Completed,
    isAssemblyStep3Completed,
    isAssemblyStep4Completed,
} from '@/modules/ConfiguratorCombinations/helpers/CombinationsHelper';

interface StepDescriptor {
    name: string;
    description: string;
}

interface UseCombinationsStepNavigationOptions {
    /** Active steps for the current product/configurator variant. */
    activeSteps: ComputedRef<StepDescriptor[]>;
    /** PID map (LEFT_GUIDE_PID, ASSEMBLY_POSITION_PID, …). */
    FilteredCombinationsOptions: Ref<any>;
    /** Form data backing the table & dropdowns — mutated when stepping back. */
    productRowsFormData: Ref<CombinationsConfiguratorFormData[]>;
    /** Selected product form ids (drives step 1 completion). */
    productsSelectedForCombination: Ref<number[]>;
    /** Selected product rows in display order (drives step 2 completion). */
    filteredProductRowsFormData: ComputedRef<CombinationsConfiguratorFormData[]>;
    /** Combined width used to prefill `totalWidth` when entering step 3. */
    combinedWidth: ComputedRef<number>;
    /** Total width — set/cleared on step transitions. */
    totalWidth: Ref<number>;
    /** Guide— cleared when stepping back from step 4. */
    guideType: Ref<string>;
    secondGuideType: Ref<string>;
}

/**
 * Owns wizard navigation for the combinations configurator, including the
 * step-transition side effects (prefill on enter, clear on leave).
 *
 * Knows about which form fields belong to which step via the PID map; the
 * popup component only needs to hand over the underlying state refs.
 */
export function useCombinationsStepNavigation({
    activeSteps,
    FilteredCombinationsOptions,
    productRowsFormData,
    productsSelectedForCombination,
    filteredProductRowsFormData,
    combinedWidth,
    totalWidth,
    guideType,
    secondGuideType,
}: UseCombinationsStepNavigationOptions) {
    // ------------------------------------------------------------------------
    // Reactive state
    // ------------------------------------------------------------------------
    const currentStep = ref<number>(1);
    const displayNextArrow = ref<boolean>(true);
    const displayPrevArrow = ref<boolean>(false);

    // ------------------------------------------------------------------------
    // Step completion
    // ------------------------------------------------------------------------
    const isStep1Completed = computed<boolean>(() =>
        isAssemblyStep1Completed(productsSelectedForCombination.value.length),
    );

    const isStep2Completed = computed<boolean>(() =>
        isAssemblyStep2Completed(
            filteredProductRowsFormData.value,
            FilteredCombinationsOptions.value.ASSEMBLY_POSITION_PID.toString(),
            isStep1Completed.value,
        ),
    );

    const isStep3Completed = computed<boolean>(() =>
        isAssemblyStep3Completed(totalWidth.value, isStep2Completed.value),
    );

    const isStep4Completed = computed<boolean>(() =>
        isAssemblyStep4Completed(
            activeSteps.value.length,
            isStep3Completed.value,
            guideType.value,
            filteredProductRowsFormData.value,
            FilteredCombinationsOptions.value.DRIVE_PID.toString(),
        ),
    );

    const stepCompletions = computed<boolean[]>(() => [
        isStep1Completed.value,
        isStep2Completed.value,
        isStep3Completed.value,
        isStep4Completed.value,
    ]);

    // ------------------------------------------------------------------------
    // Derived
    // ------------------------------------------------------------------------
    const isLastStep = computed<boolean>(() => currentStep.value === activeSteps.value.length);

    const currentStepDescription = computed<string>(() => {
        // The steps array is 0-indexed, but currentStep is 1-indexed
        const stepIndex = currentStep.value - 1;

        if (stepIndex < 0 || stepIndex >= activeSteps.value.length) {
            return '';
        }

        return activeSteps.value[stepIndex].description;
    });

    // ------------------------------------------------------------------------
    // Step completion / accessibility queries
    // ------------------------------------------------------------------------
    function isStepCompletedByIndex(stepIndex: number): boolean {
        return stepCompletions.value[stepIndex] ?? false;
    }

    function isStepAccessible(stepIndex: number): boolean {
        // First step is always accessible
        if (stepIndex === 0) return true;

        return isStepCompletedByIndex(stepIndex - 1);
    }

    function generateStepsAccessibilityArray(): boolean[] {
        const result: boolean[] = [];

        for (let i = 0; i < activeSteps.value.length; i++) {
            result.push(isStepAccessible(i));
        }

        return result;
    }

    // ------------------------------------------------------------------------
    // Arrow visibility
    // ------------------------------------------------------------------------
    function updateArrowVisibility() {
        displayNextArrow.value = currentStep.value < activeSteps.value.length;
        displayPrevArrow.value = currentStep.value > 1;
    }

    // ------------------------------------------------------------------------
    // Step transition side effects
    // ------------------------------------------------------------------------
    function clearFieldValuesByPid(pidsToClear: string[]) {
        productRowsFormData.value.forEach((row) => {
            row.formData.forEach((field) => {
                if (pidsToClear.includes(field.fieldPID)) {
                    field.fieldValue = '';
                }
            });
        });
    }

    /** Side effects fired when entering `step` going forward. */
    function applyEnterStepEffects(step: number) {
        // Prefill total width when entering the width step
        if (step === 3 && !totalWidth.value) {
            totalWidth.value = combinedWidth.value;
        }
    }

    /** Side effects fired when leaving `step` going backward. */
    function applyLeaveStepEffects(leavingStep: number) {
        const opts = FilteredCombinationsOptions.value;

        switch (leavingStep) {
            case 2:
                clearFieldValuesByPid([opts.ASSEMBLY_POSITION_PID.toString()]);
                break;
            case 3:
                totalWidth.value = 0;
                break;
            case 4:
                guideType.value = '';
                secondGuideType.value = '';
                clearFieldValuesByPid([
                    opts.LEFT_GUIDE_PID.toString(),
                    opts.RIGHT_GUIDE_PID.toString(),
                    opts.DRIVE_PID.toString(),
                    opts.COMBINATION_TYPE_PID.toString(),
                ]);
                break;
            default:
                break;
        }
    }

    // ------------------------------------------------------------------------
    // Navigation
    // ------------------------------------------------------------------------
    function goToStep(step: number) {
        const stepIndex = step - 1;

        if (isStepAccessible(stepIndex)) {
            currentStep.value = step;
            updateArrowVisibility();
        }
    }

    function goToNextStep() {
        if (currentStep.value < activeSteps.value.length) {
            currentStep.value++;
            updateArrowVisibility();
            applyEnterStepEffects(currentStep.value);
        }
    }

    function goToPrevStep() {
        applyLeaveStepEffects(currentStep.value);

        if (currentStep.value > 1) {
            currentStep.value--;
            updateArrowVisibility();
        }
    }

    // ------------------------------------------------------------------------
    // Reset
    // ------------------------------------------------------------------------
    function resetNavigation() {
        currentStep.value = 1;
        updateArrowVisibility();
    }

    return {
        // state
        currentStep,
        displayNextArrow,
        displayPrevArrow,
        // derived
        isLastStep,
        currentStepDescription,
        stepCompletions,
        // queries
        isStepCompletedByIndex,
        isStepAccessible,
        generateStepsAccessibilityArray,
        // actions
        goToStep,
        goToNextStep,
        goToPrevStep,
        updateArrowVisibility,
        resetNavigation,
    };
}
